import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronDown, Check } from "lucide-react";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/Toast";
import * as XLSX from "xlsx";
import {
  customers as customersAPI,
  surveys as surveysAPI,
  staffAssignments as staffAssignmentsAPI,
  users as usersAPI,
  clusters as clustersAPI,
  regions as regionsAPI,
  teams as teamsAPI,
  surveyReportTokens,
} from "../../lib/database";
import { generateSurveyReport } from "../customer-database/utils/generateSurveyReport";
import SurveyReportModalBody from "../customer-database/components/SurveyReportModalBody";

const UNWORKABLE_LAST_QUERY_KEY = "unworkable:lastQuery";

const DEFAULT_FILTERS = {
  search: "",
  cluster: "all",
  region: [],
  team: "all",
  teamMember: "all",
  surveyCompletedOn: "",
};

const REJECTION_REASON_LABELS = {
  insufficient_space: "Insufficient Roof Space",
  structural_issues: "Structural Issues",
  shading_problems: "Excessive Shading",
  orientation_unsuitable: "Unsuitable Orientation",
  access_restricted: "Restricted Access",
  other: "Other Reasons",
};

const Unworkable = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, userRole } = useAuth();
  const { showToast } = useToast();
  const hasInitializedFromUrlRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [surveys, setSurveys] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [clusters, setClusters] = useState([]);
  const [regions, setRegions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const [regionSearch, setRegionSearch] = useState("");
  const regionDropdownRef = useRef(null);
  const [civilUsers, setCivilUsers] = useState([]);
  const [technicalUsers, setTechnicalUsers] = useState([]);
  const [assignedMemberByCustomerId, setAssignedMemberByCustomerId] = useState(
    {},
  );
  const [downloadingSurveyId, setDownloadingSurveyId] = useState(null);
  const [viewReportModal, setViewReportModal] = useState({
    open: false,
    row: null,
    customer: null,
    survey: null,
    loading: false,
  });

  const selectedProjectId = useMemo(() => {
    if (userRole !== "super_admin") return null;
    const saved = localStorage.getItem("selectedProject");
    if (!saved || saved === "all") return null;
    return saved;
  }, [userRole]);

  const scopeFilters = useMemo(() => {
    if (!userRole) return {};
    if (userRole === "cluster_manager") {
      return { cluster_id: user?.cluster_id || null };
    }
    if (userRole === "project_admin") {
      return { project_id: user?.project_id || null };
    }
    if (userRole === "super_admin") {
      return { project_id: selectedProjectId || null };
    }
    return {};
  }, [userRole, user?.cluster_id, user?.project_id, selectedProjectId]);

  const fetchScopedUsersByRole = async (role) => {
    if (userRole === "cluster_manager" && user?.cluster_id) {
      const usersInCluster = await usersAPI.getByClusterId(user.cluster_id);
      return (usersInCluster || []).filter((u) => u.role === role);
    }
    if (userRole === "project_admin" && user?.project_id) {
      const usersInProject = await usersAPI.getByProjectId(user.project_id);
      return (usersInProject || []).filter((u) => u.role === role);
    }
    if (userRole === "super_admin" && selectedProjectId) {
      const usersInProject = await usersAPI.getByProjectId(selectedProjectId);
      return (usersInProject || []).filter((u) => u.role === role);
    }
    return await usersAPI.getByRole(role);
  };

  const readFiltersFromUrl = (params = searchParams) => {
    const next = { ...DEFAULT_FILTERS };
    const search = params.get("search");
    if (search != null && search !== "") next.search = search;
    const cluster = params.get("cluster");
    if (cluster != null && cluster !== "") next.cluster = cluster;
    const region = params.get("region");
    if (region != null && region !== "")
      next.region = region
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    const team = params.get("team");
    if (team != null && team !== "") next.team = team;
    const teamMember = params.get("teamMember");
    if (teamMember != null && teamMember !== "") next.teamMember = teamMember;
    const surveyCompletedOn =
      params.get("surveyCompletedOn") ?? params.get("surveyUpdatedOn");
    if (surveyCompletedOn != null && surveyCompletedOn !== "")
      next.surveyCompletedOn = surveyCompletedOn;
    return next;
  };

  const writeStateToUrl = (nextFilters) => {
    const params = {};
    if (nextFilters?.search?.trim()) params.search = nextFilters.search.trim();
    if (nextFilters?.cluster !== "all") params.cluster = nextFilters.cluster;
    if (Array.isArray(nextFilters?.region) && nextFilters.region.length > 0)
      params.region = nextFilters.region.join(",");
    if (nextFilters?.team !== "all") params.team = nextFilters.team;
    if (nextFilters?.teamMember !== "all")
      params.teamMember = nextFilters.teamMember;
    if (
      nextFilters?.surveyCompletedOn &&
      nextFilters.surveyCompletedOn.trim() !== ""
    )
      params.surveyCompletedOn = nextFilters.surveyCompletedOn.trim();
    try {
      const nextQuery = new URLSearchParams(params).toString();
      localStorage.setItem(UNWORKABLE_LAST_QUERY_KEY, nextQuery);
    } catch {
      // ignore
    }
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    if (hasInitializedFromUrlRef.current) return;
    let paramsToRead = searchParams;
    if (!searchParams?.toString()) {
      try {
        const savedQuery = localStorage.getItem(UNWORKABLE_LAST_QUERY_KEY);
        if (savedQuery) {
          paramsToRead = new URLSearchParams(savedQuery);
          setSearchParams(savedQuery, { replace: true });
        }
      } catch {
        // ignore
      }
    }
    const next = readFiltersFromUrl(paramsToRead);
    setFilters(next);
    setAppliedFilters(next);
    hasInitializedFromUrlRef.current = true;
  }, []);

  useEffect(() => {
    if (!hasInitializedFromUrlRef.current) return;
    writeStateToUrl(appliedFilters);
  }, [appliedFilters]);

  useEffect(() => {
    if (!regionDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (
        regionDropdownRef.current &&
        !regionDropdownRef.current.contains(e.target)
      ) {
        setRegionDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [regionDropdownOpen]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const apiScope = { ...scopeFilters };
      if (appliedFilters.cluster !== "all")
        apiScope.cluster_id = appliedFilters.cluster;

      let clustersData = [];
      let regionsData = [];
      let teamsData = [];
      if (userRole === "cluster_manager" && user?.cluster_id) {
        const cluster = await clustersAPI
          .getById(user.cluster_id)
          .catch(() => null);
        clustersData = cluster ? [cluster] : [];
        regionsData = cluster?.project_id
          ? await regionsAPI.getByProjectId(cluster.project_id).catch(() => [])
          : [];
        teamsData = await teamsAPI
          .getByClusterId(user.cluster_id)
          .catch(() => []);
      } else if (userRole === "project_admin" && user?.project_id) {
        const projId = user.project_id;
        [clustersData, regionsData, teamsData] = await Promise.all([
          clustersAPI.getByProjectId(projId).catch(() => []),
          regionsAPI.getByProjectId(projId).catch(() => []),
          teamsAPI.getAll().catch(() => []),
        ]);
      } else if (userRole === "super_admin") {
        const projId = selectedProjectId;
        if (projId) {
          [clustersData, regionsData, teamsData] = await Promise.all([
            clustersAPI.getByProjectId(projId).catch(() => []),
            regionsAPI.getByProjectId(projId).catch(() => []),
            teamsAPI.getAll().catch(() => []),
          ]);
        } else {
          [clustersData, regionsData, teamsData] = await Promise.all([
            clustersAPI.getAll().catch(() => []),
            regionsAPI.getAll().catch(() => []),
            teamsAPI.getAll().catch(() => []),
          ]);
        }
      }
      setClusters(clustersData || []);
      setRegions(regionsData || []);
      setTeams(teamsData || []);

      const [surveyList, civUsers, techUsers] = await Promise.all([
        surveysAPI.getUnworkable(apiScope),
        fetchScopedUsersByRole("civil"),
        fetchScopedUsersByRole("technical"),
      ]);

      setSurveys(surveyList || []);
      setCivilUsers(civUsers || []);
      setTechnicalUsers(techUsers || []);

      const customerIds = [
        ...new Set((surveyList || []).map((r) => r.customer_id)),
      ].filter(Boolean);
      const assignmentMap = {};
      if (customerIds.length > 0) {
        const assignments = await staffAssignmentsAPI
          .getByCustomerId(customerIds)
          .catch(() => []);
        (assignments || []).forEach((a) => {
          if (a.customer_id && a.user_id) {
            if (!assignmentMap[a.customer_id])
              assignmentMap[a.customer_id] = [];
            assignmentMap[a.customer_id].push(a.user_id);
          }
        });
      }
      setAssignedMemberByCustomerId(assignmentMap);
    } catch (err) {
      console.error("Error fetching unworkable data:", err);
      setSurveys([]);
      setAssignedMemberByCustomerId({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    user?.id,
    userRole,
    scopeFilters?.project_id,
    scopeFilters?.cluster_id,
    appliedFilters.cluster,
  ]);

  useEffect(() => {
    if (userRole !== "super_admin") return;
    const handler = () => fetchData();
    window.addEventListener("projectChanged", handler);
    return () => window.removeEventListener("projectChanged", handler);
  }, [userRole, selectedProjectId]);

  const matchesFilters = (row) => {
    const customer = row.customers;
    if (!customer) return false;
    const selectedRegionIds = Array.isArray(appliedFilters.region)
      ? appliedFilters.region
      : [];
    if (
      selectedRegionIds.length > 0 &&
      !selectedRegionIds.includes(customer.region_id)
    )
      return false;
    if (appliedFilters.team === "not_assigned") {
      if (customer.team_id) return false;
    } else if (
      appliedFilters.team !== "all" &&
      customer.team_id !== appliedFilters.team
    ) {
      return false;
    }
    if (appliedFilters.teamMember !== "all") {
      const assignedUserIds = assignedMemberByCustomerId[customer.id] || [];
      if (appliedFilters.teamMember === "not_assigned") {
        if (assignedUserIds.length > 0) return false;
      } else if (!assignedUserIds.includes(appliedFilters.teamMember)) {
        return false;
      }
    }
    const q = (appliedFilters.search || "").trim().toLowerCase();
    if (q) {
      const name = (customer.name || "").toLowerCase();
      const cin = (customer.cin || "").toLowerCase();
      if (!name.includes(q) && !cin.includes(q)) return false;
    }
    const surveyOn = (appliedFilters.surveyCompletedOn || "").trim();
    if (surveyOn && row.submitted_at) {
      const rowSubmitted = new Date(row.submitted_at).getTime();
      if (surveyOn.includes("T")) {
        const dateStr = surveyOn.slice(0, 10);
        const timeStr = surveyOn.slice(11, 19) || "";
        const isMidnight =
          !timeStr || timeStr === "00:00" || timeStr.startsWith("00:00:");
        const dayStart = new Date(dateStr + "T00:00:00").getTime();
        const until = isMidnight
          ? new Date(dateStr + "T23:59:59.999").getTime()
          : new Date(surveyOn).getTime();
        if (rowSubmitted < dayStart || rowSubmitted > until) return false;
      } else {
        const rowDate = row.submitted_at.slice(0, 10);
        if (rowDate !== surveyOn) return false;
      }
    }
    return true;
  };

  const filteredSurveys = useMemo(
    () => surveys.filter(matchesFilters),
    [
      surveys,
      appliedFilters.region,
      appliedFilters.team,
      appliedFilters.teamMember,
      appliedFilters.search,
      appliedFilters.surveyCompletedOn,
      assignedMemberByCustomerId,
    ],
  );

  const teamMemberFilterOptions = useMemo(() => {
    const byId = new Map();
    (civilUsers || []).forEach((u) => byId.set(u.id, u));
    (technicalUsers || []).forEach((u) => byId.set(u.id, u));
    const all = Array.from(byId.values());
    return [
      { value: "all", label: "All Team Members" },
      { value: "not_assigned", label: "Not Assigned" },
      ...all.map((u) => ({
        value: u.id,
        label: `${u.unique_id || ""}${u.unique_id ? " - " : ""}${
          u.name || "Unnamed"
        } (${u.teams?.name ?? "—"})`,
      })),
    ];
  }, [civilUsers, technicalUsers]);

  const handleSearch = () => {
    setAppliedFilters(filters);
  };

  const handleClearFiltersAndSearch = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  const hasActiveFilters =
    (filters.search || "").trim() !== "" ||
    filters.cluster !== "all" ||
    (Array.isArray(filters.region) && filters.region.length > 0) ||
    filters.team !== "all" ||
    filters.teamMember !== "all" ||
    (filters.surveyCompletedOn || "").trim() !== "";

  const regionOptions =
    regions?.map((r) => ({ value: r.id, label: r.name })) || [];
  const selectedRegionIds = Array.isArray(filters.region) ? filters.region : [];
  const filteredRegionOptions = regionSearch.trim()
    ? regionOptions.filter((opt) =>
        opt.label.toLowerCase().includes(regionSearch.toLowerCase()),
      )
    : regionOptions;
  const toggleRegion = (regionId) => {
    const next = selectedRegionIds.includes(regionId)
      ? selectedRegionIds.filter((id) => id !== regionId)
      : [...selectedRegionIds, regionId];
    setFilters((prev) => ({ ...prev, region: next }));
  };
  const regionDisplayLabel =
    selectedRegionIds.length === 0
      ? "All Regions"
      : selectedRegionIds.length === 1
        ? (regionOptions.find((o) => o.value === selectedRegionIds[0])?.label ??
          "1 selected")
        : `${selectedRegionIds.length} regions selected`;

  const getRejectionReasonLabel = (value) => {
    if (!value) return "—";
    return REJECTION_REASON_LABELS[value] || value;
  };

  const getRegionName = (regionId) => {
    if (!regionId) return "—";
    const region = regions.find((r) => r.id === regionId);
    return region?.name || "—";
  };

  const handleExportXlsx = async () => {
    if (filteredSurveys.length === 0) {
      showToast("No data to export", "info");
      return;
    }

    const baseUrl =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "";

    const customerIdsWithSurvey = [
      ...new Set(filteredSurveys.map((row) => row.customer_id)),
    ].filter(Boolean);

    const surveyReportTokenMap =
      customerIdsWithSurvey.length > 0
        ? await surveyReportTokens.createForCustomers(customerIdsWithSurvey)
        : {};

    const exportData = filteredSurveys.map((row) => {
      const customer = row.customers;
      const surveyReportToken = surveyReportTokenMap[row.customer_id];
      const surveyReportLink =
        baseUrl && surveyReportToken
          ? `${baseUrl}/survey-report/${encodeURIComponent(surveyReportToken)}`
          : "";

      return {
        "Customer Name": customer?.name || "",
        CIN: customer?.cin || "",
        USN: customer?.usn || "",
        Region: getRegionName(customer?.region_id),
        Cluster: customer?.clusters?.name || "",
        Team: customer?.teams?.name || "",
        "Rejection Reason": getRejectionReasonLabel(row.roof_rejection_reason),
        "Survey report": surveyReportLink,
      };
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    const SURVEY_REPORT_COL = 7;
    for (let i = 0; i < exportData.length; i++) {
      const url = exportData[i]["Survey report"];
      if (url) {
        const cellRef = XLSX.utils.encode_cell({
          r: i + 1,
          c: SURVEY_REPORT_COL,
        });
        if (ws[cellRef]) {
          ws[cellRef].l = { Target: url, Tooltip: "Open survey report" };
        }
      }
    }
    ws["!cols"] = [
      { wch: 28 },
      { wch: 18 },
      { wch: 18 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 24 },
      { wch: 50 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, "Unworkable");
    const timestamp = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `unworkable_${timestamp}.xlsx`);
    showToast("Export downloaded", "success");
  };

  const handleOpenViewReport = async (row) => {
    setViewReportModal({
      open: true,
      row,
      customer: null,
      survey: null,
      loading: true,
    });
    const surveyId = row.id;
    const customerId = row.customer_id;
    try {
      const [customer, survey] = await Promise.all([
        customersAPI.getById(customerId),
        surveysAPI.getById(surveyId),
      ]);
      setViewReportModal((prev) => ({
        ...prev,
        customer: customer || null,
        survey: survey || null,
        loading: false,
      }));
      if (!survey) {
        showToast("No survey data found for this customer", "error");
      }
    } catch (err) {
      console.error("Load survey report failed:", err);
      showToast(err?.message || "Failed to load survey report.", "error");
      setViewReportModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleCloseViewReportModal = () => {
    setViewReportModal({
      open: false,
      row: null,
      customer: null,
      survey: null,
      loading: false,
    });
  };

  const handleDownloadSurveyReportFromModal = async () => {
    const { customer, survey } = viewReportModal;
    if (!customer || !survey) return;
    try {
      setDownloadingSurveyId(survey.id);
      showToast("Generating survey report...", "info");
      await generateSurveyReport(customer, survey, null, null);
      showToast("Survey report generated successfully", "success");
    } catch (err) {
      console.error("Download survey report failed:", err);
      showToast(err?.message || "Failed to generate survey report.", "error");
    } finally {
      setDownloadingSurveyId(null);
    }
  };

  if (loading && surveys.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading unworkable list...</p>
        </div>
      </div>
    );
  }

  const itemsCount = filteredSurveys.length;

  return (
    <>
      <Helmet>
        <title>Unworkable</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <main className="content-area">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8 flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
                  Unworkable
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Customers whose roof surveys are marked as not acceptable.
                </p>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-4 md:mb-6 space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="w-full sm:max-w-md flex gap-2 items-center">
                  <Input
                    type="search"
                    placeholder="Search by customer name or CIN..."
                    value={filters.search}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        search: e.target.value,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                    className="w-full"
                  />
                  <Button
                    variant="default"
                    size="default"
                    onClick={handleSearch}
                    className="shrink-0"
                  >
                    Search
                  </Button>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Filter" size={16} />
                  <span className="font-medium">
                    {itemsCount} customer{itemsCount !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <Select
                  label="Cluster"
                  options={[
                    { value: "all", label: "All Clusters" },
                    ...(clusters?.map((c) => ({
                      value: c.id,
                      label: c.name,
                    })) || []),
                  ]}
                  value={filters.cluster}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, cluster: value }))
                  }
                />
                <div
                  className="flex flex-col gap-1 relative"
                  ref={regionDropdownRef}
                >
                  <label className="text-sm font-medium text-foreground">
                    Region
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setRegionDropdownOpen((o) => !o);
                      if (!regionDropdownOpen) setRegionSearch("");
                    }}
                    className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${selectedRegionIds.length === 0 ? "text-muted-foreground" : "text-black dark:text-white"}`}
                  >
                    <span className="truncate text-left">
                      {regionDisplayLabel}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 transition-transform ${regionDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {regionDropdownOpen && (
                    <div className="absolute left-0 bottom-full z-[10000] mb-1 w-full min-w-[200px] max-h-[280px] bg-white dark:bg-gray-800 text-black dark:text-white border border-border rounded-md shadow-lg overflow-hidden flex flex-col">
                      <div className="p-2 border-b border-border">
                        <input
                          type="search"
                          placeholder="Search regions..."
                          value={regionSearch}
                          onChange={(e) => setRegionSearch(e.target.value)}
                          className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                          autoFocus
                        />
                      </div>
                      <div className="py-1 overflow-y-auto flex-1 min-h-0 max-h-[220px]">
                        {filteredRegionOptions.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-muted-foreground">
                            No regions match
                          </div>
                        ) : (
                          filteredRegionOptions.map((opt) => {
                            const isSelected = selectedRegionIds.includes(
                              opt.value,
                            );
                            return (
                              <div
                                key={opt.value}
                                role="option"
                                aria-selected={isSelected}
                                className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                                onClick={() => toggleRegion(opt.value)}
                              >
                                <span
                                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                    isSelected
                                      ? "bg-primary border-primary text-primary-foreground"
                                      : "border-input"
                                  }`}
                                >
                                  {isSelected ? (
                                    <Check className="h-3 w-3" />
                                  ) : null}
                                </span>
                                <span className="truncate">{opt.label}</span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
              </div>
              <Select
                label="Team"
                options={[
                  { value: "all", label: "All Teams" },
                  { value: "not_assigned", label: "Not Assigned" },
                  ...(teams?.map((t) => ({
                    value: t.id,
                    label: t.name,
                  })) || []),
                ]}
                value={filters.team}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, team: value }))
                }
              />
              <Select
                label="Team Member"
                options={teamMemberFilterOptions}
                value={filters.teamMember}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, teamMember: value }))
                }
                searchable
                placeholder="All team members"
              />
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-foreground">
                  Survey completed
                </label>
                <input
                  type="datetime-local"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={filters.surveyCompletedOn || ""}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      surveyCompletedOn: e.target.value || "",
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Shows surveys completed on this day up to the selected time.
                </p>
              </div>
            </div>
              {hasActiveFilters && (
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    iconPosition="left"
                    onClick={handleClearFiltersAndSearch}
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <h2 className="text-lg md:text-xl font-semibold text-foreground">
                  Unworkable ({itemsCount})
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleExportXlsx}
                  disabled={itemsCount === 0}
                >
                  Export
                </Button>
              </div>

              {itemsCount > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Customer
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          CIN
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          USN
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Region
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Cluster
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Team
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Rejection Reason
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredSurveys.map((row) => {
                        const customer = row.customers;
                        return (
                          <tr key={row.id}>
                            <td className="px-4 py-3 text-sm text-foreground">
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/customer-detail/${row.customer_id}`,
                                  )
                                }
                                className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                              >
                                {customer?.name || "—"}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground font-mono">
                              {customer?.cin || "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground font-mono">
                              {customer?.usn || "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground">
                              {getRegionName(customer?.region_id)}
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground">
                              {customer?.clusters?.name || "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground">
                              {customer?.teams?.name || "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground">
                              {getRejectionReasonLabel(
                                row.roof_rejection_reason,
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  iconName="FileText"
                                  iconPosition="left"
                                  onClick={() => handleOpenViewReport(row)}
                                  loading={
                                    (viewReportModal.open &&
                                      viewReportModal.row?.id === row.id &&
                                      viewReportModal.loading) ||
                                    downloadingSurveyId === row.id
                                  }
                                  disabled={
                                    viewReportModal.open &&
                                    viewReportModal.loading &&
                                    viewReportModal.row?.id !== row.id
                                  }
                                >
                                  View Report
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon
                    name="XCircle"
                    size={48}
                    className="mx-auto mb-4 text-muted-foreground"
                  />
                  <p className="text-muted-foreground">
                    No unworkable customers found.
                  </p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* View Survey Report Modal */}
      {viewReportModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border shrink-0">
              <h2 className="text-xl font-semibold text-foreground">
                Survey Report
                {viewReportModal.customer?.name && (
                  <span className="text-muted-foreground font-normal ml-2">
                    — {viewReportModal.customer.name}
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={handleCloseViewReportModal}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0">
              {viewReportModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
                </div>
              ) : viewReportModal.survey && viewReportModal.customer ? (
                <SurveyReportModalBody
                  survey={viewReportModal.survey}
                  customer={viewReportModal.customer}
                  getRejectionReasonLabel={getRejectionReasonLabel}
                />
              ) : !viewReportModal.loading ? (
                <div className="text-center py-12 text-muted-foreground">
                  No survey data available.
                </div>
              ) : null}
            </div>

            {/* Footer: Download report only */}
            {viewReportModal.survey &&
              viewReportModal.customer &&
              !viewReportModal.loading && (
                <div className="shrink-0 flex flex-wrap items-center justify-end gap-3 p-4 md:p-6 border-t border-border bg-muted/30">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    onClick={handleDownloadSurveyReportFromModal}
                    loading={downloadingSurveyId === viewReportModal.survey?.id}
                  >
                    Download report
                  </Button>
                </div>
              )}
          </div>
        </div>
      )}
    </>
  );
};

export default Unworkable;
